import { useState, type FormEvent } from 'react';
import { Alert, Box, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../api/client';
import { useAuthConfig, useDevLogin } from '../api/hooks';
import { SpiritG, Wordmark } from '../components/Brand';
import { describeError } from '../components/Notify';
import { BRAND } from '../theme';
import { EmailCodeSignIn } from './EmailCodeSignIn';

const REASONS: Record<string, string> = {
  not_invited: 'Your account has not been invited to Loom. Ask a super admin to invite you.',
  pending: 'Your request is awaiting approval. An administrator has been notified and you will receive an email once it is decided.',
  denied: 'Your request was denied. The reason was emailed to you.',
  inactive: 'Your account has been deactivated. Contact a super admin.',
  wrong_domain: 'Please sign in with your school Google Workspace account. Trustees can use the Google account of the email they were invited with.',
  unverified: 'Your Google account email is not verified.',
  invalid_state: 'The sign-in session expired or was tampered with. Please try again.',
  oauth_failed: 'Google sign-in failed. Please try again.',
  access_denied: 'Google sign-in was cancelled.',
};

function ReasonAlert({ code, googleEnabled, returnTo }: { code: string; googleEnabled: boolean; returnTo: string }) {
  return (
    <Alert
      severity={code === 'pending' ? 'info' : 'error'}
      sx={{ mb: 2 }}
      data-testid="login-reason"
      action={
        code === 'denied' && googleEnabled ? (
          <Button color="inherit" size="small" href={`/api/auth/google/login?returnTo=${encodeURIComponent(returnTo)}&requestAccess=1`}>
            Request access again
          </Button>
        ) : undefined
      }
    >
      {REASONS[code] ?? `Sign-in failed (${code}).`}
    </Alert>
  );
}

const devLoginReason = (err: unknown) => (err instanceof ApiError && err.status === 403 && Object.hasOwn(REASONS, err.message) ? err.message : null);

function safeReturnTo(v: string | null) {
  return v && v.startsWith('/') && !v.startsWith('//') ? v : '/';
}

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const config = useAuthConfig();
  const devLogin = useDevLogin();
  const [email, setEmail] = useState('');
  const [codeMode, setCodeMode] = useState(false);
  const returnTo = safeReturnTo(params.get('returnTo'));
  const error = params.get('error');
  const devReason = devLoginReason(devLogin.error);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    devLogin.mutate(email, {
      onSuccess: () => navigate(returnTo, { replace: true }),
      onError: (err) => console.error('Dev login failed', err),
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: BRAND.bg, p: 2, position: 'relative', overflow: 'hidden' }}>
      <SpiritG size={720} sx={{ position: 'absolute', right: '-6vw', bottom: '-12vh', opacity: 0.05, pointerEvents: 'none', display: { xs: 'none', md: 'block' } }} />
      <Paper sx={{ p: 4, width: '100%', maxWidth: 420, position: 'relative' }} component="main">
        <Stack alignItems="flex-start" gap={2} mb={3}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <SpiritG size={48} />
            <Wordmark height={26} />
          </Stack>
          <Box sx={{ pt: 2, borderTop: `1px solid ${BRAND.line}`, alignSelf: 'stretch' }}>
            <Typography variant="h1">Loom</Typography>
            <Typography variant="body2" color="text.secondary">Admissions applicant pipeline</Typography>
          </Box>
        </Stack>
        {codeMode && config.data?.emailCodeEnabled ? (
          <EmailCodeSignIn returnTo={returnTo} onBack={() => setCodeMode(false)} />
        ) : (
          <>
            {error && <ReasonAlert code={error} googleEnabled={!!config.data?.googleEnabled} returnTo={returnTo} />}
            {config.error && <Alert severity="error" sx={{ mb: 2 }}>Could not load sign-in options: {describeError(config.error)}</Alert>}
            {config.data?.googleEnabled && (
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={<GoogleIcon />}
                href={`/api/auth/google/login?returnTo=${encodeURIComponent(returnTo)}`}
              >
                Sign in with Google
              </Button>
            )}
            {config.data?.emailCodeEnabled && (
              <Button
                fullWidth
                size="large"
                variant={config.data.googleEnabled ? 'outlined' : 'contained'}
                startIcon={<MailOutlineIcon />}
                onClick={() => setCodeMode(true)}
                sx={{ mt: config.data.googleEnabled ? 1.5 : 0 }}
              >
                Trustee: email me a sign-in code
              </Button>
            )}
            {config.data && !config.data.googleEnabled && !config.data.emailCodeEnabled && !config.data.devLoginEnabled && (
              <Alert severity="warning">No sign-in method is configured for this environment.</Alert>
            )}
            {config.data?.devLoginEnabled && (
              <>
                {(config.data.googleEnabled || config.data.emailCodeEnabled) && <Divider sx={{ my: 3 }}>or</Divider>}
                <Box component="form" onSubmit={submit} aria-label="Development sign-in">
                  <Typography variant="body2" color="text.secondary" mb={1.5}>
                    Development sign-in (local and test environments only)
                  </Typography>
                  <TextField
                    label="Email"
                    type="email"
                    required
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    sx={{ mb: 2 }}
                  />
                  {devReason && <ReasonAlert code={devReason} googleEnabled={config.data.googleEnabled} returnTo={returnTo} />}
                  {devLogin.error && !devReason && <Alert severity="error" sx={{ mb: 2 }}>{describeError(devLogin.error)}</Alert>}
                  <Button type="submit" variant="outlined" fullWidth disabled={devLogin.isPending}>
                    Sign in
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
